import { z } from "zod/v4";
import { orderType, patients } from "./domain.js";

// UI state — which screen is open and any unfinished order form. It is not a
// clinical record: no revision, no receipt, never read by a tool. Kept in
// its own key so a keystroke never rewrites the record snapshot.
export const WORKSPACE_KEY = "careline-ehr-workspace-v1";
const pages = [
  "overview",
  "history",
  "results",
  "result-detail",
  "past-visit",
  "medications",
  "background",
  "encounter",
  "orders",
  "order-compose",
  "order-review",
  "summary",
] as const;
const composerSchema = z
  .object({
    type: orderType,
    order_id: z.string().max(200).nullable(),
    data: z.record(z.string().max(100), z.string().max(8000)),
  })
  .strict();
const workspaceSchema = z
  .object({
    patient_id: z.enum(patients.map((p) => p.id) as [string, ...string[]]),
    page: z.enum(pages),
    global_page: z.enum(["chart", "schedule", "patients", "inbox"]),
    filter: z.enum(["All", "Laboratory", "Imaging", "Diagnostic test"]),
    selected_result: z.number().int().min(0).max(4),
    selected_visit: z.number().int().min(0).max(3),
    order_type: orderType,
    edit_order_id: z.string().max(200).nullable(),
    composers: z.record(z.string(), composerSchema),
  })
  .strict();
export type Workspace = z.infer<typeof workspaceSchema>;
export type Composer = z.infer<typeof composerSchema>;
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
export const defaultWorkspace = (): Workspace => ({
  patient_id: patients[0].id,
  page: "overview",
  global_page: "chart",
  filter: "All",
  selected_result: 0,
  selected_visit: 0,
  order_type: "Laboratory",
  edit_order_id: null,
  composers: {},
});

/** Restore the last workspace; anything unreadable falls back to the start screen (it is view state, not data). */
export function loadWorkspace(storage?: Storage): Workspace {
  try {
    const raw = storage?.getItem(WORKSPACE_KEY);
    if (raw) return workspaceSchema.parse(JSON.parse(raw));
  } catch {
    /* start fresh */
  }
  return defaultWorkspace();
}
export function saveWorkspace(storage: Storage | undefined, workspace: Workspace) {
  try {
    storage?.setItem(WORKSPACE_KEY, JSON.stringify(workspaceSchema.parse(workspace)));
  } catch {
    /* view state is best effort */
  }
}
export function clearWorkspace(storage?: Storage) {
  try {
    storage?.removeItem(WORKSPACE_KEY);
  } catch {
    /* best effort */
  }
}
